// =============================================================================
// Module Project - Repository: Project
// Seul fichier autorisé à importer supabase pour les opérations projet
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Project } from '../types';
import {
  getCurrentProjectAccessContext,
  getExplicitlySharedProjectIds,
  getSharedSpaceIds,
} from './projectAccessContextRepository';

// Explicit column list to avoid PostgREST schema cache issues with newer columns
const PROJECT_COLUMNS = 'id, org_id, name, description, status, start_date, due_date, logo_url, space_id, original_space_id, block_blocked_tasks, visibility, position, created_at, created_by, updated_at, updated_by';

interface HasVisibility {
  id: string;
  visibility?: string;
  space_id?: string | null;
}

async function filterProjectsForCurrentUser<T extends HasVisibility>(
  orgId: string,
  projects: T[]
): Promise<T[]> {
  if (projects.length === 0) return [];

  const accessContext = await getCurrentProjectAccessContext(orgId);
  if (!accessContext.memberId) return [];
  if (accessContext.isAdmin) return projects;

  const explicitProjectIds = await getExplicitlySharedProjectIds(
    accessContext.memberId,
    projects.map((project) => project.id)
  );

  if (accessContext.isGuest) {
    const sharedSpaceIds = await getSharedSpaceIds(
      accessContext.memberId,
      projects
        .map((project) => project.space_id)
        .filter((spaceId): spaceId is string => typeof spaceId === 'string' && spaceId.length > 0)
    );

    return projects.filter(
      (project) =>
        explicitProjectIds.has(project.id) ||
        (project.visibility !== 'private' && !!project.space_id && sharedSpaceIds.has(project.space_id))
    );
  }

  return projects.filter(
    (project) => project.visibility !== 'private' || explicitProjectIds.has(project.id)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROJECT
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchProject(projectId: string, orgId: string): Promise<Project> {
  const { data, error } = await supabase
    .from('project_projects')
    .select(PROJECT_COLUMNS)
    .eq('id', projectId)
    .eq('org_id', orgId)
    .maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new Error('Projet introuvable');
  }

  const [project] = await filterProjectsForCurrentUser(orgId, [data as Project]);
  if (!project) {
    throw new Error('Projet inaccessible');
  }

  return project;
}

export async function fetchAccessibleProjectOrgId(projectId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('project_projects')
    .select('org_id')
    .eq('id', projectId)
    .maybeSingle();

  if (error) throw error;
  return data?.org_id ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE PROJECT
// ─────────────────────────────────────────────────────────────────────────────
export async function updateProject(
  projectId: string,
  updates: Partial<Project>,
  userId?: string
): Promise<Project> {
  // Validate date coherence
  if (updates.start_date && updates.due_date && updates.start_date > updates.due_date) {
    throw new Error('La date de début ne peut pas être postérieure à la date de fin');
  }

  // Get current project data for space tracking
  const { data: currentProject } = await supabase
    .from('project_projects')
    .select('space_id, org_id, original_space_id, status')
    .eq('id', projectId)
    .single();

  // If archiving, store original space and move to archive space while preserving the business status
  if (updates.status === 'archived' && currentProject) {
    const archiveSpaceId = await getArchiveSpaceId(projectId);
    if (archiveSpaceId) {
      // Check if current space is NOT the archive space (to store original)
      const { data: currentSpace } = await supabase
        .from('project_spaces')
        .select('is_archive')
        .eq('id', currentProject.space_id || '')
        .single();

      if (!currentSpace?.is_archive) {
        // Store the original space before archiving
        updates.original_space_id = currentProject.space_id;
      }
      updates.space_id = archiveSpaceId;
      // Preserve the business status (completed, on_hold) instead of overwriting with 'archived'
      // Only keep 'archived' if the project was 'active' (standalone archive)
      if (currentProject.status !== 'active' && currentProject.status !== 'archived') {
        updates.status = currentProject.status as any;
      }
    }
  }

  // If restoring from archive, restore to original space (or default if no original)
  if (updates.status && updates.status !== 'archived' && currentProject) {
    // Check if currently in archive space
    const { data: currentSpace } = await supabase
      .from('project_spaces')
      .select('is_archive')
      .eq('id', currentProject.space_id || '')
      .single();

    if (currentSpace?.is_archive) {
      // Restore to original space if available, otherwise default space
      if (currentProject.original_space_id) {
        updates.space_id = currentProject.original_space_id;
      } else {
        const defaultSpaceId = await getDefaultSpaceId(currentProject.org_id);
        if (defaultSpaceId) {
          updates.space_id = defaultSpaceId;
        }
      }
      // Clear the original_space_id after restoring
      updates.original_space_id = null;
    }
  }

  const { data, error } = await supabase
    .from('project_projects')
    .update({ ...updates, updated_by: userId })
    .eq('id', projectId)
    .select(PROJECT_COLUMNS)
    .single();

  if (error) throw error;
  return data as Project;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get Archive Space ID for a project
// ─────────────────────────────────────────────────────────────────────────────
async function getArchiveSpaceId(projectId: string): Promise<string | null> {
  // First get project's org_id
  const { data: project } = await supabase
    .from('project_projects')
    .select('org_id')
    .eq('id', projectId)
    .single();

  if (!project) return null;

  // Then get the archive space for this org
  const { data: archiveSpace } = await supabase
    .from('project_spaces')
    .select('id')
    .eq('org_id', project.org_id)
    .eq('is_archive', true)
    .single();

  return archiveSpace?.id || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: Get Default Space ID for an org
// ─────────────────────────────────────────────────────────────────────────────
async function getDefaultSpaceId(orgId: string): Promise<string | null> {
  const { data: defaultSpace } = await supabase
    .from('project_spaces')
    .select('id')
    .eq('org_id', orgId)
    .eq('is_default', true)
    .single();

  return defaultSpace?.id || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// DELETE PROJECT
// ─────────────────────────────────────────────────────────────────────────────
export async function deleteProject(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('project_projects')
    .delete()
    .eq('id', projectId);

  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ALL PROJECTS (for sidebar - includes active and archived)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchAllProjects(orgId: string): Promise<Project[]> {
  // Try the preferred ordering (position then name). If the API schema cache
  // does not yet know the `position` column (e.g. just-deployed migration),
  // fall back to ordering by name only so the sidebar never goes blank.
  let { data, error } = await supabase
    .from('project_projects')
    .select(PROJECT_COLUMNS)
    .eq('org_id', orgId)
    .order('position', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.warn('[fetchAllProjects] position order failed, falling back to name only:', error.message);
    const fallback = await supabase
      .from('project_projects')
      .select(PROJECT_COLUMNS)
      .eq('org_id', orgId)
      .order('name', { ascending: true });
    if (fallback.error) throw fallback.error;
    data = fallback.data;
  }

  return await filterProjectsForCurrentUser(orgId, (data || []) as Project[]);
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ACTIVE PROJECTS (for backward compatibility)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchActiveProjects(orgId: string): Promise<Project[]> {
  let { data, error } = await supabase
    .from('project_projects')
    .select(PROJECT_COLUMNS)
    .eq('org_id', orgId)
    .is('original_space_id', null)
    .neq('status', 'archived')
    .order('position', { ascending: true })
    .order('name', { ascending: true });

  if (error) {
    console.warn('[fetchActiveProjects] position order failed, falling back to name only:', error.message);
    const fallback = await supabase
      .from('project_projects')
      .select(PROJECT_COLUMNS)
      .eq('org_id', orgId)
      .is('original_space_id', null)
      .neq('status', 'archived')
      .order('name', { ascending: true });
    if (fallback.error) throw fallback.error;
    data = fallback.data;
  }

  return await filterProjectsForCurrentUser(orgId, (data || []) as Project[]);
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH ACTIVE PROJECTS (for command palette)
// ─────────────────────────────────────────────────────────────────────────────
export interface ProjectSearchResult {
  id: string;
  name: string;
}

export async function searchActiveProjects(
  orgId: string,
  query: string,
  limit: number = 5
): Promise<ProjectSearchResult[]> {
  const { data, error } = await supabase
    .from('project_projects')
    .select('id, name, visibility')
    .eq('org_id', orgId)
    .is('original_space_id', null)
    .eq('status', 'active')
    .ilike('name', `%${query}%`)
    .order('name')
    .limit(Math.max(limit * 5, 20));

  if (error) throw error;

  const visibleProjects = await filterProjectsForCurrentUser(
    orgId,
    ((data || []) as Array<ProjectSearchResult & HasVisibility>)
  );

  return visibleProjects
    .map(({ id, name }) => ({ id, name }))
    .slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// SEARCH ORGANIZATION MEMBERS (for command palette)
// ─────────────────────────────────────────────────────────────────────────────
export interface MemberSearchResult {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export async function searchOrganizationMembers(
  orgId: string,
  query: string,
  limit: number = 5
): Promise<MemberSearchResult[]> {
  if (!query || query.trim().length === 0) return [];

  // First fetch members
  const { data: membersData, error: membersError } = await supabase
    .from('core_organization_members')
    .select('id, user_id')
    .eq('org_id', orgId)
    .eq('status', 'active');

  if (membersError) throw membersError;
  if (!membersData || membersData.length === 0) return [];

  const userIds = membersData.map((m) => m.user_id);

  // Fetch profiles with search
  const { data: profilesData, error: profilesError } = await supabase
    .from('core_profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', userIds)
    .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
    .limit(limit);

  if (profilesError) throw profilesError;

  const profileMap = new Map((profilesData || []).map((p) => [p.id, p]));

  return membersData
    .filter((m) => profileMap.has(m.user_id))
    .map((m) => {
      const profile = profileMap.get(m.user_id)!;
      return {
        id: m.id,
        user_id: m.user_id,
        full_name: profile.full_name,
        email: profile.email,
        avatar_url: profile.avatar_url,
      };
    })
    .slice(0, limit);
}

// ─────────────────────────────────────────────────────────────────────────────
// DRAG & DROP: REORDER PROJECTS WITHIN A SPACE
// Persists the workspace-shared ordering of projects in a given space bucket.
// ─────────────────────────────────────────────────────────────────────────────
export async function reorderProjectsInSpace(orderedProjectIds: string[]): Promise<void> {
  if (orderedProjectIds.length === 0) return;
  const { error } = await supabase.rpc('reorder_projects_in_space', {
    _project_ids: orderedProjectIds,
  });
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// DRAG & DROP: MOVE A PROJECT TO ANOTHER SPACE AT A GIVEN INDEX
// Permission check (creator or Owner/Admin) is enforced server-side.
// ─────────────────────────────────────────────────────────────────────────────
export async function moveProjectToSpace(
  projectId: string,
  targetSpaceId: string | null,
  newIndex: number
): Promise<void> {
  const { error } = await supabase.rpc('move_project_to_space', {
    _project_id: projectId,
    _target_space_id: targetSpaceId,
    _new_index: newIndex,
  });
  if (error) throw error;
}
